/**
 * BattleScreen Component
 *
 * Displays real-time battle animation between player Pokemon and opponent.
 * Shows HP bars, stamina, battle log, and playback speed controls.
 * Features a dynamic battle arena with live battle feed.
 */

import React, { useRef, useEffect, useState, useMemo, useCallback } from 'react';
import { Clock, Zap, Swords, Shield, Sparkles, Wind, Heart, Activity, FileText } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useGame } from '../contexts/GameContext';
import { useCareer } from '../contexts/CareerContext';
import { useInventory } from '../contexts/InventoryContext';
import { useAuth } from '../contexts/AuthContext';
import {
  generatePokemonSprite,
  getGradeColor,
  getPokemonGrade,
  getEvolutionFormForGrade
} from '../utils/gameUtils';
import { TypeBadge } from '../components/TypeIcon';
import { ICONS, EVOLUTION_CHAINS, EVOLUTION_CONFIG } from '../shared/gameData';
import { getGymLeaderImage } from '../constants/trainerImages';
import WeatherTerrainDisplay from '../components/WeatherTerrainDisplay';
import KOAnimation from '../components/KOAnimation';

/**
 * Inline StatIcon component for battle stats display
 */
const BattleStatIcon = ({ stat, size = 12 }) => {
  const icons = {
    Attack: <Swords size={size} className="text-orange-500" />,
    Defense: <Shield size={size} className="text-blue-500" />,
    Instinct: <Sparkles size={size} className="text-purple-500" />,
    Speed: <Wind size={size} className="text-green-500" />
  };
  return icons[stat] || null;
};

/**
 * Status effect configuration with colors and display names
 */
const STATUS_EFFECTS_CONFIG = {
  burn: { label: 'Burned', bgColor: 'bg-orange-500', textColor: 'text-white' },
  poison: { label: 'Poisoned', bgColor: 'bg-purple-600', textColor: 'text-white' },
  badly_poison: { label: 'Toxic', bgColor: 'bg-purple-600', textColor: 'text-white' },
  badlypoison: { label: 'Toxic', bgColor: 'bg-purple-600', textColor: 'text-white' },
  frozen: { label: 'Frozen', bgColor: 'bg-cyan-400', textColor: 'text-white' },
  freeze: { label: 'Frozen', bgColor: 'bg-cyan-400', textColor: 'text-white' },
  paralyzed: { label: 'Paralyzed', bgColor: 'bg-yellow-400', textColor: 'text-gray-800' },
  paralyze: { label: 'Paralyzed', bgColor: 'bg-yellow-400', textColor: 'text-gray-800' },
  stun: { label: 'Stunned', bgColor: 'bg-amber-500', textColor: 'text-white' },
  stunned: { label: 'Stunned', bgColor: 'bg-amber-500', textColor: 'text-white' },
  sleep: { label: 'Asleep', bgColor: 'bg-indigo-400', textColor: 'text-white' },
  confused: { label: 'Confused', bgColor: 'bg-pink-400', textColor: 'text-white' },
  confuse: { label: 'Confused', bgColor: 'bg-pink-400', textColor: 'text-white' },
  flinch: { label: 'Flinched', bgColor: 'bg-gray-500', textColor: 'text-white' },
  curse: { label: 'Cursed', bgColor: 'bg-pink-500', textColor: 'text-white' },
};

/**
 * Get display config for a status effect
 */
const getStatusConfig = (effectType) => {
  const normalizedType = effectType?.toLowerCase().replace(/[-_]/g, '');
  return STATUS_EFFECTS_CONFIG[normalizedType] || {
    label: effectType || 'Unknown',
    bgColor: 'bg-gray-400',
    textColor: 'text-white'
  };
};

/**
 * StatusLabels component - displays status effect badges
 * Shows "Status OK!" in grey when no harmful status effects are active
 */
const StatusLabels = ({ statusEffects = [] }) => {
  // Filter to only show harmful status effects (not buffs/debuffs which show on stats)
  const harmfulStatuses = statusEffects.filter(effect => {
    const type = effect.type?.toLowerCase() || '';
    // Exclude buff/debuff effects since those already show on stats
    return !type.includes('buff') && !type.includes('debuff') && type !== '';
  });

  if (harmfulStatuses.length === 0) {
    return (
      <div className="flex items-center gap-1">
        <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-gray-400 text-white whitespace-nowrap">
          OK!
        </span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1 flex-wrap">
      {harmfulStatuses.map((effect, idx) => {
        const config = getStatusConfig(effect.type);
        return (
          <motion.span
            key={`${effect.type}-${idx}`}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            className={`px-2 py-0.5 rounded text-[10px] font-semibold ${config.bgColor} ${config.textColor} whitespace-nowrap`}
          >
            {config.label}
          </motion.span>
        );
      })}
    </div>
  );
};

/**
 * Check if a Pokemon is incapacitated by a status effect
 */
const isIncapacitated = (statusEffects = []) => {
  const incapacitatingStatuses = ['stun', 'stunned', 'sleep', 'frozen', 'paralyzed', 'paralyze', 'flinch'];
  return statusEffects.some(effect => {
    const type = effect.type?.toLowerCase() || '';
    return incapacitatingStatuses.includes(type);
  });
};

/**
 * ActionLabel component - displays the current action (move name with damage/heal amount)
 * Format: "[Move name] [damage]" or "[Move name] +[heal]" (green for heals)
 * Now rendered inline, not floating. Stays visible constantly, only updates text.
 * Shows "Disabled!" when Pokemon is incapacitated by status effects.
 */
const ActionLabel = ({ action, statusEffects = [] }) => {
  // Check if Pokemon is incapacitated
  const disabled = isIncapacitated(statusEffects);

  // Track the last non-null action to keep displaying it
  const lastActionRef = React.useRef(action || { moveName: 'Ready' });
  if (action) {
    lastActionRef.current = action;
  }

  // Normalize action to object format (handle legacy string format)
  const normalizeAction = (act) => {
    if (!act) return { moveName: 'Ready' };
    if (typeof act === 'string') return { moveName: act };
    return act;
  };

  const currentAction = disabled ? { moveName: 'Disabled!' } : normalizeAction(action || lastActionRef.current);
  const { moveName, damage, isHeal, isMiss } = currentAction;

  // Build display text: "[Move name] [damage]" format
  let displayText = moveName;
  if (isMiss) {
    displayText = `${moveName} (Miss)`;
  } else if (damage != null && damage > 0) {
    displayText = isHeal ? `${moveName} +${damage}` : `${moveName} ${damage}`;
  }

  const getActionStyle = () => {
    if (moveName === 'Disabled!') return 'bg-red-600 text-white';
    if (!moveName || moveName === 'Ready') return 'bg-gray-400 text-white';
    if (moveName.toLowerCase() === 'rest') return 'bg-blue-500 text-white';
    if (isMiss) return 'bg-gray-400 text-white';
    if (isHeal) return 'bg-green-500 text-white';
    return 'bg-pocket-red text-white';
  };

  // Dynamic font size based on text length - larger on PC for readability
  const getFontSize = (text) => {
    if (!text) return 'text-[8px] sm:text-sm';
    const len = text.length;
    if (len > 20) return 'text-[6px] sm:text-xs';
    if (len > 15) return 'text-[7px] sm:text-sm';
    return 'text-[8px] sm:text-sm';
  };

  return (
    <motion.span
      key={displayText}
      initial={false}
      animate={{ scale: [1, 1.05, 1] }}
      transition={{ duration: 0.15 }}
      className={`px-2 sm:px-4 py-0.5 sm:py-1.5 rounded ${getFontSize(displayText)} font-bold shadow-sm whitespace-nowrap overflow-hidden text-ellipsis max-w-full ${getActionStyle()}`}
    >
      {displayText}
    </motion.span>
  );
};

// NOTE: Inspiration generation removed - now handled server-side
// Backend generates inspirations in routes/career/progression.js POST /complete
// Using B+ aptitude filtering with weighted star rarity (20% 1★, 70% 2★, 10% 3★)

/**
 * Calculate stat multipliers from status effects
 * Returns an object with combined multiplier for each stat
 */
const getStatMultipliers = (statusEffects = []) => {
  const multipliers = {
    Attack: 1,
    Defense: 1,
    Instinct: 1,
    Speed: 1
  };

  statusEffects.forEach(effect => {
    if (effect.type === 'buff_attack' && effect.multiplier) {
      multipliers.Attack *= effect.multiplier;
    }
    if (effect.type === 'debuff_attack' && effect.multiplier) {
      multipliers.Attack *= effect.multiplier;
    }
    if (effect.type === 'buff_defense' && effect.multiplier) {
      multipliers.Defense *= effect.multiplier;
    }
    if (effect.type === 'debuff_defense' && effect.multiplier) {
      multipliers.Defense *= effect.multiplier;
    }
    if (effect.type === 'buff_instinct' && effect.multiplier) {
      multipliers.Instinct *= effect.multiplier;
    }
    if (effect.type === 'debuff_instinct' && effect.multiplier) {
      multipliers.Instinct *= effect.multiplier;
    }
    if (effect.type === 'buff_speed' && effect.multiplier) {
      multipliers.Speed *= effect.multiplier;
    }
    if (effect.type === 'debuff_speed' && effect.multiplier) {
      multipliers.Speed *= effect.multiplier;
    }
  });

  return multipliers;
};

/**
 * StatDisplay component - shows modified stat value in green (buffed) or red (debuffed)
 * Compact display for mobile: icon + value only, no label text
 */
const StatDisplay = ({ label, stat, value, multiplier }) => {
  const modifiedValue = Math.round(value * multiplier);
  const isBuffed = multiplier > 1;
  const isDebuffed = multiplier < 1;

  return (
    <span className="flex items-center justify-center gap-0.5 whitespace-nowrap">
      <BattleStatIcon stat={stat} size={10} />
      <span className={`${isBuffed ? 'text-green-500 font-bold' : isDebuffed ? 'text-red-500 font-bold' : ''}`}>
        {modifiedValue}
      </span>
    </span>
  );
};

/**
 * FloatingDamage component - shows damage numbers floating up
 */
const FloatingDamage = ({ damage, isCrit, isHeal, x = 50, onComplete }) => {
  if (!damage) return null;

  return (
    <motion.div
      initial={{ opacity: 1, y: 0, scale: isCrit ? 1.8 : 1.2 }}
      animate={{ opacity: [1, 1, 0], y: -60, scale: isCrit ? 2 : 1.4 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 1.2, ease: 'easeOut', times: [0, 0.6, 1] }}
      onAnimationComplete={onComplete}
      className={`absolute top-1/4 left-1/2 -translate-x-1/2 pointer-events-none font-bold text-2xl z-30 ${
        isHeal ? 'text-green-500' : isCrit ? 'text-yellow-400' : 'text-red-500'
      }`}
      style={{ textShadow: '2px 2px 6px rgba(0,0,0,0.7), 0 0 10px rgba(0,0,0,0.5)' }}
    >
      {isHeal ? '+' : '-'}{damage}
      {isCrit && <span className="text-sm ml-1">CRIT!</span>}
    </motion.div>
  );
};

/**
 * TypeEffectivenessIndicator - removed to prevent action label truncation
 * Type effectiveness is shown through colored sprites and battle log instead
 */
const TypeEffectivenessIndicator = ({ effectiveness }) => {
  // Disabled to prevent action label text from being cut off
  return null;
};

/**
 * StatusParticles - animated particles for status effects
 */
const StatusParticles = ({ statusEffects = [] }) => {
  // Create a string key from status effects to detect changes
  const effectsKey = statusEffects.map(e => e.type).join(',');

  const particles = useMemo(() => {
    const result = [];
    statusEffects.forEach((effect, idx) => {
      const type = effect.type?.toLowerCase() || '';
      if (type.includes('burn')) {
        for (let i = 0; i < 3; i++) {
          result.push({
            key: `burn-${idx}-${i}`,
            type: 'burn',
            delay: i * 0.3,
            x: 20 + (idx * 15 + i * 20) % 60,
            y: 30 + (i * 25) % 40
          });
        }
      } else if (type.includes('poison') || type.includes('badly_poison')) {
        for (let i = 0; i < 3; i++) {
          result.push({
            key: `poison-${idx}-${i}`,
            type: 'poison',
            delay: i * 0.4,
            x: 25 + (idx * 18 + i * 22) % 50,
            y: 35 + (i * 20) % 30
          });
        }
      } else if (type.includes('frozen') || type.includes('freeze')) {
        for (let i = 0; i < 4; i++) {
          result.push({
            key: `frozen-${idx}-${i}`,
            type: 'frozen',
            delay: i * 0.2,
            x: 15 + (idx * 12 + i * 18) % 70,
            y: 25 + (i * 18) % 50
          });
        }
      } else if (type.includes('paralyz')) {
        for (let i = 0; i < 3; i++) {
          result.push({
            key: `paralyz-${idx}-${i}`,
            type: 'paralyz',
            delay: i * 0.15,
            x: 30 + (idx * 20 + i * 15) % 40,
            y: 40 + (i * 15) % 20
          });
        }
      } else if (type.includes('sleep')) {
        for (let i = 0; i < 3; i++) {
          result.push({
            key: `sleep-${idx}-${i}`,
            type: 'sleep',
            delay: i * 0.5,
            x: 50 + (i * 10),
            y: 20 + (i * 10)
          });
        }
      } else if (type.includes('confuse')) {
        for (let i = 0; i < 3; i++) {
          result.push({
            key: `confuse-${idx}-${i}`,
            type: 'confuse',
            delay: i * 0.3,
            x: 35 + (i * 15),
            y: 25 + (i * 10)
          });
        }
      }
    });
    return result;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectsKey]);

  if (particles.length === 0) return null;

  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      {particles.map(({ key, type, delay, x, y }) => {
        if (type === 'burn') {
          return (
            <motion.div
              key={key}
              className="absolute text-lg"
              style={{ left: `${x}%`, top: `${y}%` }}
              initial={{ opacity: 0, y: 0, scale: 0.5 }}
              animate={{
                opacity: [0, 1, 0],
                y: [-10, -30],
                scale: [0.5, 1.2, 0.3]
              }}
              transition={{
                duration: 1.2,
                delay,
                repeat: Infinity,
                repeatDelay: 0.5
              }}
            >
              🔥
            </motion.div>
          );
        }
        if (type === 'poison') {
          return (
            <motion.div
              key={key}
              className="absolute text-sm"
              style={{ left: `${x}%`, top: `${y}%` }}
              initial={{ opacity: 0, y: 0, scale: 0.8 }}
              animate={{
                opacity: [0, 0.8, 0],
                y: [-5, -25],
                scale: [0.8, 1.3, 0.5]
              }}
              transition={{
                duration: 1.5,
                delay,
                repeat: Infinity,
                repeatDelay: 0.3
              }}
            >
              💀
            </motion.div>
          );
        }
        if (type === 'frozen') {
          return (
            <motion.div
              key={key}
              className="absolute text-sm"
              style={{ left: `${x}%`, top: `${y}%` }}
              initial={{ opacity: 0, rotate: 0 }}
              animate={{
                opacity: [0, 1, 0.5],
                rotate: [0, 180, 360],
                scale: [0.5, 1.2, 0.8]
              }}
              transition={{
                duration: 2,
                delay,
                repeat: Infinity
              }}
            >
              ❄️
            </motion.div>
          );
        }
        if (type === 'paralyz') {
          return (
            <motion.div
              key={key}
              className="absolute text-sm"
              style={{ left: `${x}%`, top: `${y}%` }}
              initial={{ opacity: 0 }}
              animate={{
                opacity: [0, 1, 0],
                scale: [0.5, 1.8, 0.5]
              }}
              transition={{
                duration: 0.3,
                delay,
                repeat: Infinity,
                repeatDelay: 0.8
              }}
            >
              ⚡
            </motion.div>
          );
        }
        if (type === 'sleep') {
          return (
            <motion.div
              key={key}
              className="absolute text-lg font-bold text-indigo-400"
              style={{ left: `${x}%`, top: `${y}%` }}
              initial={{ opacity: 0, y: 0 }}
              animate={{
                opacity: [0, 1, 0],
                y: [-5, -25],
                scale: [0.8, 1.2, 0.6]
              }}
              transition={{
                duration: 1.8,
                delay,
                repeat: Infinity,
                repeatDelay: 0.2
              }}
            >
              Z
            </motion.div>
          );
        }
        if (type === 'confuse') {
          return (
            <motion.div
              key={key}
              className="absolute text-sm"
              style={{ left: `${x}%`, top: `${y}%` }}
              initial={{ opacity: 0, rotate: 0 }}
              animate={{
                opacity: [0, 1, 0],
                rotate: [0, 360, 720],
                scale: [0.6, 1, 0.6]
              }}
              transition={{
                duration: 1.5,
                delay,
                repeat: Infinity,
                repeatDelay: 0.3
              }}
            >
              💫
            </motion.div>
          );
        }
        return null;
      })}
    </div>
  );
};

/**
 * LiveBattleFeed - scrolling feed of battle events with visual flair
 */
const LiveBattleFeed = ({ displayLog, maxEntries = 6 }) => {
  const feedRef = useRef(null);

  useEffect(() => {
    if (feedRef.current) {
      feedRef.current.scrollTop = feedRef.current.scrollHeight;
    }
  }, [displayLog?.length]);

  const recentEntries = (displayLog || []).slice(-maxEntries);

  const getEntryStyle = (type) => {
    switch (type) {
      case 'crit':
        return 'bg-gradient-to-r from-red-500/20 to-yellow-500/20 border-l-4 border-red-500 text-red-600';
      case 'hit':
        return 'bg-red-50 border-l-4 border-red-400 text-red-600';
      case 'miss':
        return 'bg-blue-50 border-l-4 border-blue-400 text-blue-600';
      case 'victory':
        return 'bg-gradient-to-r from-green-100 to-emerald-100 border-l-4 border-green-500 text-green-700';
      case 'defeat':
        return 'bg-gradient-to-r from-orange-100 to-red-100 border-l-4 border-orange-500 text-orange-700';
      case 'tie':
        return 'bg-gradient-to-r from-yellow-100 to-amber-100 border-l-4 border-yellow-500 text-amber-700 font-bold';
      default:
        return 'bg-gray-50 border-l-4 border-gray-300 text-pocket-text-light';
    }
  };

  if (!recentEntries.length) {
    return (
      <div className="text-center text-pocket-text-light py-4 text-sm">
        Battle starting...
      </div>
    );
  }

  // Transform text to remove "for" from damage messages (e.g., "for 12 damage" -> "12 damage")
  const formatBattleText = (text) => {
    return text.replace(/for (\d+) damage/gi, '$1 damage');
  };

  return (
    <div ref={feedRef} className="space-y-1.5 overflow-y-auto max-h-40">
      {recentEntries.map((entry, idx) => (
        <motion.div
          key={`${displayLog.length}-${idx}`}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.2 }}
          className={`py-2 px-3 rounded-lg text-sm font-medium ${getEntryStyle(entry.type)}`}
        >
          {formatBattleText(entry.text)}
        </motion.div>
      ))}
    </div>
  );
};

/**
 * Parse the action for a combatant from a message string
 * Messages use Pokemon names, combined with " | " separator, e.g.:
 * "Bulbasaur used VineWhip for 13 damage! | Charmander is resting..."
 *
 * Returns an object with move name and optional damage/heal amount
 * @param {string} message - The battle log message
 * @param {string} pokemonName - The Pokemon name to search for
 * @returns {{ moveName: string, damage?: number, isHeal?: boolean } | null}
 */
const getActionFromMessage = (message, pokemonName) => {
  if (!message || !pokemonName) return null;

  // Escape special regex characters in Pokemon name
  const escapedName = pokemonName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  // Split by " | " to handle multiple actions in one tick
  const parts = message.split(' | ');

  for (const part of parts) {
    const trimmedPart = part.trim();

    // Check if this part is about the specified Pokemon
    if (trimmedPart.startsWith(pokemonName)) {
      if (trimmedPart.includes('is resting')) {
        return { moveName: 'Rest', isHeal: false };
      }

      // Match "PokemonName used MoveName for X damage!" or "PokemonName used MoveName!"
      const useRegex = new RegExp(`${escapedName} used ([^!]+?)(?:\\s+for\\s+(\\d+)\\s+damage)?!`);
      const match = trimmedPart.match(useRegex);
      if (match) {
        const moveName = match[1].trim();
        const damage = match[2] ? parseInt(match[2]) : null;

        if (trimmedPart.includes('missed') || trimmedPart.includes('dodged')) {
          return { moveName, damage: null, isMiss: true };
        }

        return { moveName, damage, isHeal: false };
      }

      // Check for heal moves: "PokemonName recovered X HP!"
      const healRegex = new RegExp(`${escapedName} recovered (\\d+) HP`);
      const healMatch = trimmedPart.match(healRegex);
      if (healMatch) {
        const healAmount = parseInt(healMatch[1]);
        // Try to find the move name from earlier in the same message
        const moveMatch = trimmedPart.match(new RegExp(`${escapedName} used ([^!]+?)!`));
        const moveName = moveMatch ? moveMatch[1].trim() : 'Heal';
        return { moveName, damage: healAmount, isHeal: true };
      }
    }
  }

  return null;
};

const BattleScreen = ({ onBattleComplete }) => {
  const { battleState, battleSpeed, setBattleSpeed, setGameState, setBattleState, setCareerHistory, setCompletedCareerData, setEvolutionModal } = useGame();
  const { careerData, completeCareer, consumePokeclock } = useCareer();
  const { loadPrimos } = useInventory();
  const { authToken } = useAuth();
  const battleLogRef = useRef(null);
  const [showPokeclockModal, setShowPokeclockModal] = useState(false);
  const [playerAction, setPlayerAction] = useState(null);
  const [opponentAction, setOpponentAction] = useState(null);

  // Visual enhancement states
  const [playerDamage, setPlayerDamage] = useState(null);
  const [opponentDamage, setOpponentDamage] = useState(null);
  const [playerHit, setPlayerHit] = useState(false);
  const [opponentHit, setOpponentHit] = useState(false);
  const [playerEffectiveness, setPlayerEffectiveness] = useState(null);
  const [opponentEffectiveness, setOpponentEffectiveness] = useState(null);
  // Badge state: 'vs' | 'tie' | 'run-it-back' - controls the center badge display
  const [badgeState, setBadgeState] = useState('vs');
  const damageKeyRef = useRef(0);

  // KO animation state
  const [showKOAnimation, setShowKOAnimation] = useState(false);

  // Battle stats tracking - use refs to avoid stale closure issues at high speed
  const [battleStats, setBattleStats] = useState({
    player: { totalDamage: 0, hitsLanded: 0, crits: 0, misses: 0, timesRested: 0 },
    opponent: { totalDamage: 0, hitsLanded: 0, crits: 0, misses: 0, timesRested: 0 }
  });
  const battleStatsRef = useRef(battleStats);
  const lastProcessedLogIndex = useRef(-1);

  // Keep battleStatsRef in sync
  useEffect(() => {
    battleStatsRef.current = battleStats;
  }, [battleStats]);

  // Reset when battle state is cleared (new battle starting)
  useEffect(() => {
    if (!battleState) {
      setPlayerAction(null);
      setOpponentAction(null);
      setPlayerDamage(null);
      setOpponentDamage(null);
      setPlayerHit(false);
      setOpponentHit(false);
      setPlayerEffectiveness(null);
      setOpponentEffectiveness(null);
      setBadgeState('vs');
      const initialStats = {
        player: { totalDamage: 0, hitsLanded: 0, crits: 0, misses: 0, timesRested: 0 },
        opponent: { totalDamage: 0, hitsLanded: 0, crits: 0, misses: 0, timesRested: 0 }
      };
      setBattleStats(initialStats);
      battleStatsRef.current = initialStats;
      lastProcessedLogIndex.current = -1;
    }
  }, [battleState]);

  // Note: Pokeclock tutorial modal is now handled in CareerScreen BEFORE battle starts
  // The showTutorial flag in battleState is still present but no longer used here

  // Process log entries - batched to handle high speed playback
  // Uses actual Pokemon names since BattleController replaces "Player 1/2" before displayLog
  // Returns separate damage info for player and opponent to handle simultaneous hits
  // IMPORTANT: Messages may contain multiple attacks in one tick, separated by " | "
  // e.g., "Bulbasaur used VineWhip for 13 damage! | Charmander used Ember for 10 damage!"
  const processLogEntry = useCallback((entry, accumulatedStats, playerName, opponentName) => {
    const message = entry?.text || '';
    const isCritEntry = entry.type === 'crit';
    const playerNameLower = (playerName || '').toLowerCase();
    const opponentNameLower = (opponentName || '').toLowerCase();

    // Track damage to each target separately (not just "last" damage)
    let playerDamageInfo = null;  // Damage taken BY player (opponent attacked)
    let opponentDamageInfo = null;  // Damage taken BY opponent (player attacked)
    let playerEffectiveness = null;
    let opponentEffectiveness = null;

    // Split message by " | " to handle multiple actions in one tick
    const parts = message.split(' | ');

    // DEBUG: Log what we're processing
    if (parts.length > 1 || message.includes('damage')) {
      console.log('[processLogEntry] Processing:', { message, parts, playerNameLower, opponentNameLower });
    }

    for (const part of parts) {
      const partLower = part.toLowerCase();
      const isCrit = isCritEntry || partLower.includes('critical');

      // Parse damage from this part
      const damageMatch = part.match(/for (\d+) damage/i);
      const damage = damageMatch ? parseInt(damageMatch[1]) : null;

      // Check who used the move in this part
      const playerUsedInPart = playerNameLower && partLower.includes(`${playerNameLower} used`);
      const opponentUsedInPart = opponentNameLower && partLower.includes(`${opponentNameLower} used`);

      // DEBUG: Log detected attacks
      if (damage) {
        console.log('[processLogEntry] Found damage in part:', { part, damage, playerUsedInPart, opponentUsedInPart });
      }

      if (damage) {
        if (playerUsedInPart) {
          // Player attacked, so opponent takes damage
          accumulatedStats.player.totalDamage += damage;
          accumulatedStats.player.hitsLanded += 1;
          accumulatedStats.player.crits += isCrit ? 1 : 0;
          opponentDamageInfo = { value: damage, isCrit };
        } else if (opponentUsedInPart) {
          // Opponent attacked, so player takes damage
          accumulatedStats.opponent.totalDamage += damage;
          accumulatedStats.opponent.hitsLanded += 1;
          accumulatedStats.opponent.crits += isCrit ? 1 : 0;
          playerDamageInfo = { value: damage, isCrit };
        }
      }

      // Track misses in this part
      if (partLower.includes('missed') || partLower.includes('dodged')) {
        if (playerUsedInPart) {
          accumulatedStats.player.misses += 1;
        } else if (opponentUsedInPart) {
          accumulatedStats.opponent.misses += 1;
        }
      }

      // Track rests in this part
      if (partLower.includes('resting')) {
        if (playerNameLower && partLower.includes(playerNameLower)) {
          accumulatedStats.player.timesRested += 1;
        } else if (opponentNameLower && partLower.includes(opponentNameLower)) {
          accumulatedStats.opponent.timesRested += 1;
        }
      }

      // Track effectiveness for this part (separate for each side)
      if (partLower.includes('super effective')) {
        if (playerUsedInPart) {
          opponentEffectiveness = 'super';  // Player's attack was super effective against opponent
        } else if (opponentUsedInPart) {
          playerEffectiveness = 'super';  // Opponent's attack was super effective against player
        }
      } else if (partLower.includes('not very effective')) {
        if (playerUsedInPart) {
          opponentEffectiveness = 'not_very';
        } else if (opponentUsedInPart) {
          playerEffectiveness = 'not_very';
        }
      }
    }

    return { playerDamageInfo, opponentDamageInfo, playerEffectiveness, opponentEffectiveness };
  }, []);

  // Update action labels and parse battle events when displayLog changes
  useEffect(() => {
    const displayLog = battleState?.displayLog;
    if (!displayLog?.length) return;

    const currentIndex = displayLog.length - 1;
    if (currentIndex <= lastProcessedLogIndex.current) return;

    // Get Pokemon names for log parsing
    const playerName = battleState?.player?.name || '';
    const opponentName = battleState?.opponent?.name || '';

    // Create accumulated stats starting from current state (using ref to avoid stale closure)
    const accumulatedStats = {
      player: { ...battleStatsRef.current.player },
      opponent: { ...battleStatsRef.current.opponent }
    };

    // Track damage and effectiveness separately for each side
    // This allows both Pokemon to show effects when hit in the same turn
    let playerDamageInfo = null;
    let opponentDamageInfo = null;
    let playerEffectivenessInfo = null;
    let opponentEffectivenessInfo = null;

    // Process ALL new log entries in one pass
    for (let i = lastProcessedLogIndex.current + 1; i <= currentIndex; i++) {
      const entry = displayLog[i];
      const result = processLogEntry(entry, accumulatedStats, playerName, opponentName);
      // Keep the most recent damage info for each target
      if (result.playerDamageInfo) playerDamageInfo = result.playerDamageInfo;
      if (result.opponentDamageInfo) opponentDamageInfo = result.opponentDamageInfo;
      if (result.playerEffectiveness) playerEffectivenessInfo = result.playerEffectiveness;
      if (result.opponentEffectiveness) opponentEffectivenessInfo = result.opponentEffectiveness;
    }

    // Update stats in a single state update
    setBattleStats(accumulatedStats);
    battleStatsRef.current = accumulatedStats;
    lastProcessedLogIndex.current = currentIndex;

    // Show KO animation when a Pokemon is KO'd
    // The BattleController pauses when it hits a KO entry (koPaused = true)
    // We trigger the KO animation overlay here
    if (battleState.koData && battleState.koPaused && !showKOAnimation) {
      setShowKOAnimation(true);
    }

    // Show visual effects for BOTH player and opponent if they were hit
    // This fixes the issue where only one effect showed when both got hit

    if (playerDamageInfo) {
      setPlayerHit(true);
      setPlayerDamage({ value: playerDamageInfo.value, isCrit: playerDamageInfo.isCrit, key: damageKeyRef.current++ });
      setTimeout(() => setPlayerHit(false), 200);
    }

    if (opponentDamageInfo) {
      setOpponentHit(true);
      setOpponentDamage({ value: opponentDamageInfo.value, isCrit: opponentDamageInfo.isCrit, key: damageKeyRef.current++ });
      setTimeout(() => setOpponentHit(false), 200);
    }

    // Show effectiveness indicators for both sides
    if (playerEffectivenessInfo) {
      setPlayerEffectiveness(playerEffectivenessInfo);
      setTimeout(() => setPlayerEffectiveness(null), 1500);
    }

    if (opponentEffectivenessInfo) {
      setOpponentEffectiveness(opponentEffectivenessInfo);
      setTimeout(() => setOpponentEffectiveness(null), 1500);
    }

    // Get the most recent log entry's message for action labels
    const lastEntry = displayLog[displayLog.length - 1];
    const message = lastEntry?.text || '';

    // Extract weather/terrain from the latest log entry and update battleState
    if (lastEntry?.weather !== undefined) {
      setBattleState(prev => ({
        ...prev,
        weather: lastEntry.weather
      }));
    }

    // Parse actions using Pokemon names (messages have been transformed from Player 1/2)
    // Note: playerName and opponentName are already defined at the top of this useEffect
    const newPlayerAction = getActionFromMessage(message, playerName);
    const newOpponentAction = getActionFromMessage(message, opponentName);

    setPlayerAction(newPlayerAction);
    setOpponentAction(newOpponentAction);
  }, [battleState?.displayLog, battleState?.player?.name, battleState?.opponent?.name, battleState?.koPaused, processLogEntry, setBattleState, showKOAnimation]);

  // Auto-scroll battle log to bottom when new messages appear
  useEffect(() => {
    if (battleLogRef.current) {
      battleLogRef.current.scrollTop = battleLogRef.current.scrollHeight;
    }
  }, [battleState?.displayLog?.length]);

  // Refresh primos after gym leader victory (primos were awarded server-side)
  const primosRefreshedRef = useRef(false);
  useEffect(() => {
    if (!battleState) {
      primosRefreshedRef.current = false;
      return;
    }
    if (primosRefreshedRef.current) return;

    const battleOver = battleState.player.currentHP <= 0 || battleState.opponent.currentHP <= 0;
    const playerWon = battleState.winner === 'player';
    const isGymLeader = battleState.isGymLeader;

    if (battleOver && playerWon && isGymLeader) {
      primosRefreshedRef.current = true;
      loadPrimos();
    }
  }, [battleState?.player.currentHP, battleState?.opponent.currentHP, battleState, loadPrimos]);

  if (!battleState) {
    return null;
  }

  const getBattleDisplayName = (combatant) => {
    // Get the appropriate evolution form name based on grade
    const displayName = getEvolutionFormForGrade(combatant.name, combatant.stats);
    if (combatant.isGymLeader) {
      return `${displayName} (Gym Leader)`;
    }
    return displayName;
  };

  /**
   * Check if Pokemon can evolve based on grade and trigger evolution modal if so
   * This is called when returning from a won battle to check for evolution
   */
  const checkAndTriggerEvolution = () => {
    console.log('[BattleScreen->Evolution] checkAndTriggerEvolution called with careerData:', {
      hasCareerData: !!careerData,
      pokemonName: careerData?.pokemon?.name,
      currentStats: careerData?.currentStats,
      evolutionStage: careerData?.evolutionStage,
      basePokemonName: careerData?.basePokemonName
    });

    if (!careerData || !careerData.pokemon || !careerData.currentStats) {
      console.log('[BattleScreen->Evolution] Missing required data, returning early');
      return;
    }

    const pokemonName = careerData.pokemon.name;

    // Find the evolution chain this Pokemon belongs to
    let baseName = careerData.basePokemonName;
    let evolutionChain = baseName ? EVOLUTION_CHAINS[baseName] : null;

    // If basePokemonName isn't set or doesn't have a chain, search for it
    if (!evolutionChain) {
      // Check if current Pokemon name is a base form
      if (EVOLUTION_CHAINS[pokemonName]) {
        baseName = pokemonName;
        evolutionChain = EVOLUTION_CHAINS[pokemonName];
      } else {
        // Search for this Pokemon in evolution chains (as stage1 or stage2)
        for (const [base, data] of Object.entries(EVOLUTION_CHAINS)) {
          const stage1Names = Array.isArray(data.stage1) ? data.stage1 : [data.stage1];
          if (stage1Names.includes(pokemonName) || data.stage2 === pokemonName) {
            baseName = base;
            evolutionChain = data;
            break;
          }
        }
      }
    }

    if (!evolutionChain) {
      console.log('[BattleScreen->Evolution] No evolution chain found for', pokemonName, 'or base', baseName);
      return;
    }

    console.log('[BattleScreen->Evolution] Found evolution chain:', evolutionChain);

    const currentGrade = getPokemonGrade(careerData.currentStats);
    console.log('[BattleScreen->Evolution] Current grade:', currentGrade, 'for stats:', careerData.currentStats);

    // Determine current stage based on Pokemon name if evolutionStage not set
    let currentStage = careerData.evolutionStage;
    if (currentStage === undefined || currentStage === null) {
      if (pokemonName === baseName) {
        currentStage = 0;
      } else {
        const stage1Names = Array.isArray(evolutionChain.stage1) ? evolutionChain.stage1 : [evolutionChain.stage1];
        if (stage1Names.includes(pokemonName)) {
          currentStage = 1;
        } else if (evolutionChain.stage2 === pokemonName) {
          currentStage = 2;
        } else {
          currentStage = 0;
        }
      }
    }

    // Grade order for comparison
    const gradeOrder = ['F', 'F+', 'E', 'E+', 'D', 'D+', 'C', 'C+', 'B', 'B+', 'A', 'A+', 'S', 'S+', 'UU', 'UU+'];
    const gradeIndex = gradeOrder.indexOf(currentGrade);
    const cGradeIndex = gradeOrder.indexOf('C');  // Index 6
    const aGradeIndex = gradeOrder.indexOf('A');  // Index 10

    let evolutionResult = null;

    console.log('[BattleScreen->Evolution] Checking evolution:', {
      currentStage,
      gradeIndex,
      cGradeIndex,
      aGradeIndex,
      hasStage1: !!evolutionChain.stage1,
      hasStage2: !!evolutionChain.stage2,
      stage1Check: currentStage === 0 && evolutionChain.stage1 && gradeIndex >= cGradeIndex,
      stage2Check: currentStage === 1 && evolutionChain.stage2 && gradeIndex >= aGradeIndex
    });

    // Check for stage 1 evolution (at C grade or higher)
    if (currentStage === 0 && evolutionChain.stage1 && gradeIndex >= cGradeIndex) {
      const stage1Options = Array.isArray(evolutionChain.stage1) ? evolutionChain.stage1 : [evolutionChain.stage1];
      const selectedEvolution = stage1Options[Math.floor(Math.random() * stage1Options.length)];
      evolutionResult = { toName: selectedEvolution, toStage: 1 };
    }
    // Check for stage 2 evolution (at A grade or higher)
    else if (currentStage === 1 && evolutionChain.stage2 && gradeIndex >= aGradeIndex) {
      evolutionResult = { toName: evolutionChain.stage2, toStage: 2 };
    }

    if (evolutionResult) {
      const oldStats = careerData.currentStats;
      const statBoost = evolutionChain.stages === 2
        ? EVOLUTION_CONFIG.STAT_BOOST.TWO_STAGE
        : EVOLUTION_CONFIG.STAT_BOOST.ONE_STAGE;
      const newStats = {};
      for (const [s, value] of Object.entries(oldStats)) {
        newStats[s] = Math.round(value * (1 + statBoost));
      }

      // Set evolution modal - will be shown when CareerScreen mounts
      setEvolutionModal({
        fromName: careerData.pokemon.name,
        toName: evolutionResult.toName,
        toStage: evolutionResult.toStage,
        oldStats: oldStats,
        newStats: newStats
      });
    }
  };

  const handleContinue = async () => {
    const playerLost = battleState.winner === 'opponent';
    const playerWon = battleState.winner === 'player';
    const isGymLeader = battleState.isGymLeader;
    const isEliteFour = battleState.isEliteFour;
    const eliteFourIndex = battleState.eliteFourIndex;
    const isPvPReplay = battleState.isPvPReplay;
    const isTournamentReplay = battleState.isTournamentReplay;

    // If player lost to a gym leader or Elite Four, check pokeclocks
    if (playerLost && (isGymLeader || isEliteFour)) {
      const pokeclocks = careerData?.pokeclocks || 0;

      if (pokeclocks > 0) {
        // Use a pokeclock to retry the battle (server-authoritative)
        setShowPokeclockModal(true);

        // Call server to use pokeclock - this decrements pokeclocks (turn stays same)
        const result = await consumePokeclock();

        // Hide modal after 2 seconds and return to career for gym retry
        setTimeout(() => {
          setShowPokeclockModal(false);
          setBattleState(null);
          setGameState('career');
        }, 2000);

        if (!result) {
          console.error('Failed to use pokeclock on server');
        }
        return;
      } else {
        // No pokeclocks remaining - career ends
        // Server generates inspirations when completeCareer is called
        const result = await completeCareer('defeated');

        if (result && result.savedCareerData) {
          // Refresh primos balance after earning career completion reward
          loadPrimos();

          // Save completed career data for GameOver screen - use backend inspirations
          setCompletedCareerData({
            ...result.savedCareerData,
            trainedPokemon: result.trainedPokemon,
            inspirations: result.trainedPokemon.inspirations,
            victory: false,
            primosReward: result.primosReward || 0
          });

          // Add to career history
          const historyEntry = {
            pokemon: result.savedCareerData.pokemon.name,
            primaryType: result.savedCareerData.pokemon.primaryType,
            finalStats: result.savedCareerData.currentStats,
            knownAbilities: result.savedCareerData.knownAbilities,
            finalTurn: result.savedCareerData.turn,
            gymsDefeated: result.savedCareerData.currentGymIndex,
            victory: false,
            timestamp: new Date().toISOString()
          };

          setCareerHistory(prev => {
            const newHistory = [historyEntry, ...prev];
            localStorage.setItem('pokemonCareerHistory', JSON.stringify(newHistory));
            return newHistory;
          });

          setBattleState(null);
          setGameState('gameOver');
          return;
        } else {
          // completeCareer failed - fall back to using local career data or battleState
          const fallbackPokemon = careerData?.pokemon || {
            name: battleState.player?.name || 'Pokemon',
            primaryType: battleState.player?.type || 'Normal',
            typeAptitudes: {},
            strategyAptitudes: {}
          };
          const fallbackStats = careerData?.currentStats || battleState.player?.stats || {};

          setCompletedCareerData({
            pokemon: fallbackPokemon,
            currentStats: fallbackStats,
            turn: careerData?.turn,
            currentGymIndex: careerData?.currentGymIndex || 0,
            inspirations: null, // No inspirations if backend call failed
            victory: false,
            primosReward: 0
          });
          setBattleState(null);
          setGameState('gameOver');
          return;
        }
      }
    }

    // Check for Elite Four victory - if player won the 4th Elite Four battle (index 3)
    console.log('[BattleScreen] handleContinue - Checking for Elite Four victory:', {
      playerWon,
      isEliteFour,
      eliteFourIndex,
      condition: playerWon && isEliteFour && eliteFourIndex === 3
    });
    if (playerWon && isEliteFour && eliteFourIndex === 3) {
      // Player has become Champion!
      console.log('[BattleScreen] CHAMPION VICTORY DETECTED! Calling completeCareer with champion status');
      // Server generates inspirations when completeCareer is called
      const result = await completeCareer('champion');

      if (result && result.savedCareerData) {
        // Refresh primos balance after earning career completion reward
        loadPrimos();

        // Save completed career data for Victory screen - use server-generated inspirations
        setCompletedCareerData({
          ...result.savedCareerData,
          trainedPokemon: result.trainedPokemon,
          inspirations: result.trainedPokemon.inspirations, // Use backend inspirations
          victory: true,
          primosReward: result.primosReward || 0
        });

        // Add to career history
        const historyEntry = {
          pokemon: result.savedCareerData.pokemon.name,
          primaryType: result.savedCareerData.pokemon.primaryType,
          finalStats: result.savedCareerData.currentStats,
          knownAbilities: result.savedCareerData.knownAbilities,
          finalTurn: result.savedCareerData.turn,
          gymsDefeated: 5, // All gyms defeated
          victory: true,
          timestamp: new Date().toISOString()
        };

        setCareerHistory(prev => {
          const newHistory = [historyEntry, ...prev];
          localStorage.setItem('pokemonCareerHistory', JSON.stringify(newHistory));
          return newHistory;
        });

        // Check for evolution before going to victory screen
        checkAndTriggerEvolution();

        setBattleState(null);
        setGameState('victory');
        return;
      } else {
        // completeCareer failed - this is a serious issue, career is still active!
        console.error('[BattleScreen] completeCareer FAILED! Career may still be active.');
        // Don't show victory screen - go back to career and let the user try again
        // or show an error message
        alert('Failed to complete career. Please try again.');
        setBattleState(null);
        setGameState('career');
        return;
      }
    }

    // Check if this is a PvP or Tournament replay battle
    if (isPvPReplay) {
      // PvP battle - call onBattleComplete callback if provided
      if (onBattleComplete) {
        onBattleComplete(battleState.winner);
      } else {
        // Fallback: return to PvP replay screen
        setBattleState(null);
        setGameState('pvpReplay');
      }
      return;
    }

    if (isTournamentReplay) {
      // Tournament battle - call onBattleComplete callback if provided
      if (onBattleComplete) {
        onBattleComplete(battleState.winner);
      } else {
        // Fallback: return to tournament replay screen
        setBattleState(null);
        setGameState('tournamentReplay');
      }
      return;
    }

    // Normal case - player won or wasn't a gym battle, return to career
    // Check for evolution before returning (only if player won)
    if (playerWon) {
      console.log('[BattleScreen] handleContinue - About to check evolution. isEliteFour:', isEliteFour, 'eliteFourIndex:', eliteFourIndex);
      console.log('[BattleScreen] careerData at evolution check:', {
        pokemonName: careerData?.pokemon?.name,
        currentStats: careerData?.currentStats,
        evolutionStage: careerData?.evolutionStage,
        basePokemonName: careerData?.basePokemonName,
        turn: careerData?.turn
      });
      checkAndTriggerEvolution();
    }
    setBattleState(null);
    setGameState('career');
  };

  const playerPct = (battleState.player.currentHP / battleState.player.stats.HP) * 100;
  const opponentPct = (battleState.opponent.currentHP / battleState.opponent.stats.HP) * 100;
  const battleOver = battleState.player.currentHP <= 0 || battleState.opponent.currentHP <= 0;

  // Get HP bar color based on percentage
  const getHpColor = (pct) => {
    if (pct > 50) return 'bg-pocket-green'; // Green when healthy
    if (pct > 25) return 'bg-yellow-500';   // Yellow when damaged
    return 'bg-red-600';                     // Red when critical
  };

  return (
    <div className={`min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 p-3 sm:p-4 ${battleOver ? 'pb-24' : ''}`}>
      <div className="max-w-4xl mx-auto space-y-4">
        {/* Battle Header Bar */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-pocket-red via-red-500 to-orange-500 rounded-xl px-4 py-2 flex items-center justify-between shadow-lg"
        >
          <div className="flex items-center gap-2 text-white">
            <Zap size={20} className="animate-pulse" />
            <span className="font-bold text-lg">Battle Arena</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1 bg-black/20 rounded-full px-3 py-1">
              <Clock size={14} className="text-white/80" />
              <span className="text-white font-bold text-sm">Tick {battleState.tick}</span>
            </div>
            <div className="flex gap-1">
              {[1, 2, 4].map((speed) => (
                <button
                  key={speed}
                  onClick={() => setBattleSpeed(speed)}
                  className={`px-2.5 py-1 rounded-full font-bold text-xs transition-all ${
                    battleSpeed === speed
                      ? 'bg-white text-pocket-red shadow-md'
                      : 'bg-white/20 text-white hover:bg-white/30'
                  }`}
                >
                  {speed}x
                </button>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Main Battle Arena */}
        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-gradient-to-b from-emerald-800/50 via-emerald-900/30 to-slate-900/50 rounded-2xl shadow-2xl overflow-hidden border border-emerald-700/30"
        >
          {/* Weather/Terrain Display - Positioned inside battle arena at top */}
          {battleState.weather?.type && battleState.weather?.ticksRemaining > 0 && (
            <div className="flex justify-center pt-3 pb-1">
              <WeatherTerrainDisplay weather={battleState.weather} />
            </div>
          )}

          {/* Battle Field */}
          <div className="relative p-6 sm:p-8 pt-4 sm:pt-6">
            {/* Arena Background Pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_120%,rgba(34,197,94,0.3),transparent_50%)]" />
            </div>

            <div className="grid grid-cols-2 gap-6 sm:gap-16 relative z-10">
              {/* Player Side */}
              <div className="space-y-4">
                {/* Pokemon Sprite - Large and prominent */}
                <div className="flex justify-center py-4 sm:py-8">
                  <div className="relative">
                    <motion.div
                      animate={
                        battleState.player.currentHP <= 0
                          ? { opacity: 0.3, scale: 0.8, filter: 'grayscale(100%)' }
                          : playerHit
                          ? { x: [-8, 8, -8, 8, 0], opacity: 1, scale: 1, filter: ['brightness(1)', 'brightness(2.5)', 'brightness(1)'] }
                          : { y: [0, -3, 0], opacity: 1, scale: 1, filter: 'brightness(1)' }
                      }
                      transition={battleState.player.currentHP <= 0 ? { duration: 0.5 } : playerHit ? { duration: 0.2 } : { duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                    >
                      <div className="hidden sm:block">
                        {generatePokemonSprite(battleState.player.primaryType, battleState.player.name, 160)}
                      </div>
                      <div className="sm:hidden">
                        {generatePokemonSprite(battleState.player.primaryType, battleState.player.name, 180)}
                      </div>
                    </motion.div>
                    <StatusParticles statusEffects={battleState.player.statusEffects} />
                    <AnimatePresence mode="wait">
                      {playerDamage && (
                        <FloatingDamage
                          key={playerDamage.key}
                          damage={playerDamage.value}
                          isCrit={playerDamage.isCrit}
                          onComplete={() => setPlayerDamage(null)}
                        />
                      )}
                    </AnimatePresence>
                  </div>
                </div>

                {/* Player Info Card */}
                <div className="bg-white/95 backdrop-blur rounded-xl p-4 shadow-lg border-l-4 border-blue-500 mt-4">
                  {/* Name - never truncated */}
                  <h3 className="font-bold text-pocket-text text-sm mb-1">{battleState.player.name}</h3>
                  {/* Badges row */}
                  <div className="flex items-center gap-1.5 mb-2">
                    <span
                      className="px-1.5 py-0.5 rounded text-[10px] font-bold text-white"
                      style={{ backgroundColor: getGradeColor(getPokemonGrade(battleState.player.stats)) }}
                    >
                      {getPokemonGrade(battleState.player.stats)}
                    </span>
                    <TypeBadge type={battleState.player.primaryType} size={14} />
                  </div>

                  {/* Action and Status Row */}
                  <div className="flex items-center justify-between gap-1 mb-2">
                    <div className="flex-shrink-0 min-w-0">
                      <StatusLabels statusEffects={battleState.player.statusEffects} />
                    </div>
                    <div className="flex items-center gap-1 flex-shrink min-w-0 overflow-hidden">
                      <AnimatePresence>
                        {playerEffectiveness && <TypeEffectivenessIndicator effectiveness={playerEffectiveness} />}
                      </AnimatePresence>
                      <ActionLabel action={playerAction} statusEffects={battleState.player.statusEffects} />
                    </div>
                  </div>

                  {/* HP Bar - More prominent */}
                  <div className="mb-2">
                    <div className="flex justify-between text-xs text-pocket-text-light mb-1">
                      <span className="flex items-center gap-1"><Heart size={10} /> HP</span>
                      <span className="font-bold">{battleState.player.currentHP}/{battleState.player.stats.HP}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden shadow-inner">
                      <motion.div
                        className={`h-full rounded-full ${getHpColor(playerPct)} shadow-sm`}
                        initial={{ width: '100%' }}
                        animate={{ width: `${playerPct}%` }}
                        transition={{ duration: 0.3 }}
                      />
                    </div>
                  </div>

                  {/* Stamina Bar */}
                  <div className="mb-2">
                    <div className="flex justify-between text-xs text-pocket-text-light mb-1">
                      <span className="flex items-center gap-1">
                        <Activity size={10} /> Stamina
                        {battleState.player.isResting && <span className="text-blue-500">{ICONS.SLEEPING}</span>}
                      </span>
                      <span>{battleState.player.currentStamina}/100</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-amber-400 to-yellow-500 transition-all duration-300"
                        style={{ width: `${battleState.player.currentStamina}%` }}
                      />
                    </div>
                  </div>

                  {/* Stats Grid */}
                  {(() => {
                    const multipliers = getStatMultipliers(battleState.player.statusEffects);
                    return (
                      <div className="grid grid-cols-4 gap-1 text-[10px]">
                        <StatDisplay stat="Attack" value={battleState.player.stats.Attack} multiplier={multipliers.Attack} />
                        <StatDisplay stat="Defense" value={battleState.player.stats.Defense} multiplier={multipliers.Defense} />
                        <StatDisplay stat="Instinct" value={battleState.player.stats.Instinct} multiplier={multipliers.Instinct} />
                        <StatDisplay stat="Speed" value={battleState.player.stats.Speed} multiplier={multipliers.Speed} />
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* VS Badge / TIE Badge / RUN IT BACK Badge */}
              <div className="absolute left-1/2 top-1/4 -translate-x-1/2 -translate-y-1/2 z-20">
                <AnimatePresence mode="wait">
                  {badgeState === 'tie' && (
                    <motion.div
                      key="tie"
                      initial={{ scale: 0, rotate: -180 }}
                      animate={{ scale: [1, 1.3, 1.1], rotate: 0 }}
                      exit={{ scale: 0, opacity: 0 }}
                      transition={{ duration: 0.5, ease: 'easeOut' }}
                      className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-to-br from-yellow-400 to-amber-500 flex items-center justify-center text-white font-black text-xl sm:text-2xl shadow-xl border-4 border-white/50"
                    >
                      TIE!
                    </motion.div>
                  )}
                  {badgeState === 'run-it-back' && (
                    <motion.div
                      key="run-it-back"
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      exit={{ scale: 0, opacity: 0 }}
                      transition={{ duration: 0.4, ease: 'easeOut' }}
                      className="px-4 py-2 sm:px-5 sm:py-3 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-black text-sm sm:text-base shadow-xl border-4 border-white/50 whitespace-nowrap"
                    >
                      RUN IT BACK!
                    </motion.div>
                  )}
                  {badgeState === 'vs' && (
                    <motion.div
                      key="vs"
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{ scale: [1, 1.05, 1], opacity: 1, rotate: [0, 2, -2, 0] }}
                      transition={{ duration: 2, repeat: Infinity }}
                      className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-gradient-to-br from-pocket-red to-orange-500 flex items-center justify-center text-white font-black text-lg shadow-xl border-2 border-white/30"
                    >
                      VS
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Opponent Side */}
              <div className="space-y-4">
                {/* Gym Leader Portrait */}
                {battleState.opponent.isGymLeader && battleState.opponent.gymLeaderName && (
                  <div className="flex justify-center">
                    <div className="relative">
                      <img
                        src={getGymLeaderImage(battleState.opponent.gymLeaderName)}
                        alt={`${battleState.opponent.gymLeaderName} - Gym Leader`}
                        className="w-16 h-16 sm:w-20 sm:h-20 object-contain rounded-xl border-2 border-yellow-400 bg-white/90 shadow-lg"
                      />
                      <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-yellow-400 to-amber-500 text-gray-900 px-2 py-0.5 rounded-full text-[9px] font-bold shadow whitespace-nowrap">
                        Gym Leader
                      </div>
                    </div>
                  </div>
                )}

                {/* Pokemon Sprite */}
                <div className="flex justify-center py-4 sm:py-8">
                  <div className="relative">
                    <motion.div
                      animate={
                        battleState.opponent.currentHP <= 0
                          ? { opacity: 0.3, scale: 0.8, filter: 'grayscale(100%)' }
                          : opponentHit
                          ? { x: [-8, 8, -8, 8, 0], opacity: 1, scale: 1, filter: ['brightness(1)', 'brightness(2.5)', 'brightness(1)'] }
                          : { y: [0, -3, 0], opacity: 1, scale: 1, filter: 'brightness(1)' }
                      }
                      transition={battleState.opponent.currentHP <= 0 ? { duration: 0.5 } : opponentHit ? { duration: 0.2 } : { duration: 2, repeat: Infinity, ease: 'easeInOut', delay: 0.5 }}
                    >
                      <div className="hidden sm:block">
                        {generatePokemonSprite(
                          battleState.opponent.primaryType,
                          getEvolutionFormForGrade(battleState.opponent.name, battleState.opponent.stats),
                          160
                        )}
                      </div>
                      <div className="sm:hidden">
                        {generatePokemonSprite(
                          battleState.opponent.primaryType,
                          getEvolutionFormForGrade(battleState.opponent.name, battleState.opponent.stats),
                          180
                        )}
                      </div>
                    </motion.div>
                    <StatusParticles statusEffects={battleState.opponent.statusEffects} />
                    <AnimatePresence mode="wait">
                      {opponentDamage && (
                        <FloatingDamage
                          key={opponentDamage.key}
                          damage={opponentDamage.value}
                          isCrit={opponentDamage.isCrit}
                          onComplete={() => setOpponentDamage(null)}
                        />
                      )}
                    </AnimatePresence>
                  </div>
                </div>

                {/* Opponent Info Card */}
                <div className="bg-white/95 backdrop-blur rounded-xl p-4 shadow-lg border-l-4 border-pocket-red mt-4">
                  {/* Name - never truncated */}
                  <h3 className="font-bold text-pocket-text text-sm mb-1">{getBattleDisplayName(battleState.opponent)}</h3>
                  {/* Badges row */}
                  <div className="flex items-center gap-1.5 mb-2">
                    <span
                      className="px-1.5 py-0.5 rounded text-[10px] font-bold text-white"
                      style={{ backgroundColor: getGradeColor(getPokemonGrade(battleState.opponent.stats)) }}
                    >
                      {getPokemonGrade(battleState.opponent.stats)}
                    </span>
                    <TypeBadge type={battleState.opponent.primaryType} size={14} />
                  </div>

                  {/* Action and Status Row */}
                  <div className="flex items-center justify-between gap-1 mb-2">
                    <div className="flex-shrink-0 min-w-0">
                      <StatusLabels statusEffects={battleState.opponent.statusEffects} />
                    </div>
                    <div className="flex items-center gap-1 flex-shrink min-w-0 overflow-hidden">
                      <AnimatePresence>
                        {opponentEffectiveness && <TypeEffectivenessIndicator effectiveness={opponentEffectiveness} />}
                      </AnimatePresence>
                      <ActionLabel action={opponentAction} statusEffects={battleState.opponent.statusEffects} />
                    </div>
                  </div>

                  {/* HP Bar */}
                  <div className="mb-2">
                    <div className="flex justify-between text-xs text-pocket-text-light mb-1">
                      <span className="flex items-center gap-1"><Heart size={10} /> HP</span>
                      <span className="font-bold">{battleState.opponent.currentHP}/{battleState.opponent.stats.HP}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden shadow-inner">
                      <motion.div
                        className={`h-full rounded-full ${getHpColor(opponentPct)} shadow-sm`}
                        initial={{ width: '100%' }}
                        animate={{ width: `${opponentPct}%` }}
                        transition={{ duration: 0.3 }}
                      />
                    </div>
                  </div>

                  {/* Stamina Bar */}
                  <div className="mb-2">
                    <div className="flex justify-between text-xs text-pocket-text-light mb-1">
                      <span className="flex items-center gap-1">
                        <Activity size={10} /> Stamina
                        {battleState.opponent.isResting && <span className="text-blue-500">{ICONS.SLEEPING}</span>}
                      </span>
                      <span>{battleState.opponent.currentStamina}/100</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-amber-400 to-yellow-500 transition-all duration-300"
                        style={{ width: `${battleState.opponent.currentStamina}%` }}
                      />
                    </div>
                  </div>

                  {/* Stats Grid */}
                  {(() => {
                    const multipliers = getStatMultipliers(battleState.opponent.statusEffects);
                    return (
                      <div className="grid grid-cols-4 gap-1 text-[10px]">
                        <StatDisplay stat="Attack" value={battleState.opponent.stats.Attack} multiplier={multipliers.Attack} />
                        <StatDisplay stat="Defense" value={battleState.opponent.stats.Defense} multiplier={multipliers.Defense} />
                        <StatDisplay stat="Instinct" value={battleState.opponent.stats.Instinct} multiplier={multipliers.Instinct} />
                        <StatDisplay stat="Speed" value={battleState.opponent.stats.Speed} multiplier={multipliers.Speed} />
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Battle Log */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white/95 backdrop-blur rounded-xl shadow-lg overflow-hidden"
        >
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileText size={18} className="text-slate-600" />
              <span className="font-bold text-pocket-text text-sm">Battle Log</span>
            </div>
            {battleOver && (
              <motion.span
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                className={`px-3 py-0.5 rounded-full text-xs font-bold ${
                  battleState.winner === 'player'
                    ? 'bg-green-500 text-white'
                    : 'bg-red-500 text-white'
                }`}
              >
                {battleState.winner === 'player' ? 'Victory!' : 'Defeat'}
              </motion.span>
            )}
          </div>
          <div className="p-3">
            <LiveBattleFeed displayLog={battleState.displayLog} maxEntries={8} />
          </div>
        </motion.div>

        {/* Battle Summary Stats - hidden for now */}

      </div>

      {/* Continue Button - Fixed at bottom on mobile when battle is over */}
      {/* Hidden for trial battles - TrialBattleScreen handles completion */}
      {battleOver && !battleState.isTrialBattle && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="fixed bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-pocket-bg via-pocket-bg to-transparent z-40"
        >
          <div className="max-w-4xl mx-auto">
            <button
              onClick={handleContinue}
              className="w-full pocket-btn-primary py-4 text-lg shadow-lg"
            >
              {battleState.winner === 'player' ? 'Continue ▶' : (
                (battleState.isGymLeader || battleState.isEliteFour) && (careerData?.pokeclocks || 0) > 0
                  ? <span className="flex items-center justify-center gap-2">
                      Use Pokeclock <Clock size={20} /> ({careerData?.pokeclocks} left)
                    </span>
                  : 'Continue'
              )}
            </button>
          </div>
        </motion.div>
      )}

      {/* Pokeclock Modal for Gym Battle Retries */}
      {showPokeclockModal && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-2xl p-8 max-w-md w-full shadow-card-lg text-center"
          >
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-blue-100 flex items-center justify-center">
              <Clock size={48} className="text-blue-600" />
            </div>
            <h2 className="text-2xl font-bold text-pocket-text mb-2">Pokeclock Used!</h2>
            <p className="text-pocket-text-light mb-4">You get another chance!</p>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-pocket-bg rounded-full">
              <span className="text-pocket-text-light text-sm">
                {(careerData?.pokeclocks || 0)} Pokeclock{(careerData?.pokeclocks || 0) !== 1 ? 's' : ''} remaining
              </span>
            </div>
          </motion.div>
        </div>
      )}

      {/* KO Animation Overlay */}
      {showKOAnimation && battleState?.koData && (
        <KOAnimation
          koData={battleState.koData}
          playerPokemon={battleState.player}
          opponentPokemon={battleState.opponent}
          onComplete={() => {
            setShowKOAnimation(false);

            // If it was a tie, show "RUN IT BACK!" and heal both Pokemon
            if (battleState.koData.isTie) {
              // Clear visual states immediately
              setPlayerHit(false);
              setOpponentHit(false);
              setPlayerDamage(null);
              setOpponentDamage(null);
              setPlayerEffectiveness(null);
              setOpponentEffectiveness(null);

              setBadgeState('run-it-back');
              setTimeout(() => {
                // Heal both Pokemon and resume playback
                setBattleState(prev => ({
                  ...prev,
                  player: {
                    ...prev.player,
                    currentHP: prev.player.stats.HP,
                    statusEffects: [] // Clear status effects on heal
                  },
                  opponent: {
                    ...prev.opponent,
                    currentHP: prev.opponent.stats.HP,
                    statusEffects: [] // Clear status effects on heal
                  },
                  koPaused: false, // Resume playback
                  koData: null // Clear KO data
                }));
                setBadgeState('vs');
                // Hide VS badge after a moment
                setTimeout(() => {
                  setBadgeState(null);
                }, 1500);
              }, 1500);
            } else {
              // Normal KO - just resume battle playback
              setBattleState(prev => ({
                ...prev,
                koPaused: false
              }));
            }
          }}
          onSkip={() => {
            setShowKOAnimation(false);

            // If it was a tie, skip straight to heal and resume
            if (battleState.koData.isTie) {
              // Clear visual states
              setPlayerHit(false);
              setOpponentHit(false);
              setPlayerDamage(null);
              setOpponentDamage(null);
              setPlayerEffectiveness(null);
              setOpponentEffectiveness(null);

              setBattleState(prev => ({
                ...prev,
                player: {
                  ...prev.player,
                  currentHP: prev.player.stats.HP,
                  statusEffects: []
                },
                opponent: {
                  ...prev.opponent,
                  currentHP: prev.opponent.stats.HP,
                  statusEffects: []
                },
                koPaused: false,
                koData: null
              }));
            } else {
              // Normal KO - just resume
              setBattleState(prev => ({
                ...prev,
                koPaused: false
              }));
            }
          }}
        />
      )}
    </div>
  );
};

export default BattleScreen;

